'use client';

import { useEffect, useRef } from 'react';

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const DEFAULT_DURATION = 0.5;
const DEFAULT_STAGGER = 0.08;
const DEFAULT_EASE = 'power4.in';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

function prefersReducedMotion() {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const REFRESH_DEBOUNCE_MS = 150;

/**
 * Всяка карта се анимира когато влезе във видимата област при скрол.
 * Перформанс: само transform + opacity (GPU), RAF за batch, refresh с debounce при resize.
 */
export default function StaggerReveal({
  children,
  duration = DEFAULT_DURATION,
  stagger = DEFAULT_STAGGER,
  ease = DEFAULT_EASE,
  className,
  start = 'top 88%',
  /**
   * Сменя ли се (напр. при пренареждане/сортиране на децата) — преизчисляваме
   * позициите на тригерите, така че разместените карти, попаднали във видимата
   * област, да се „разкрият“ (иначе остават с opacity:0 и не се виждат).
   */
  refreshKey,
}) {
  const ref = useRef(null);
  const lastAnimatedRef = useRef(-1);
  const pendingRef = useRef(new Set());
  const rafRef = useRef(null);

  useEffect(() => {
    const container = ref.current;
    if (!container) {
      return undefined;
    }

    const target = container.firstElementChild;
    if (!target?.children?.length) {
      return undefined;
    }

    const items = Array.from(target.children);
    const pendingSet = pendingRef.current;

    if (prefersReducedMotion()) {
      gsap.set(items, { y: 0, opacity: 1 });
      return undefined;
    }

    const flushBatch = () => {
      if (pendingSet.size === 0) {
        return;
      }
      const maxIndex = Math.max(...pendingSet);
      const last = lastAnimatedRef.current;
      if (maxIndex <= last) {
        pendingSet.clear();
        return;
      }
      const from = last + 1;
      const batch = items.slice(from, maxIndex + 1);
      pendingSet.clear();
      lastAnimatedRef.current = maxIndex;

      gsap.to(batch, {
        y: 0,
        opacity: 1,
        duration,
        stagger,
        ease,
        overwrite: 'auto',
      });
    };

    const triggers = [];

    items.forEach((item, index) => {
      const t = ScrollTrigger.create({
        trigger: item,
        start,
        once: true,
        onEnter: () => {
          if (index <= lastAnimatedRef.current) {
            return;
          }
          pendingSet.add(index);

          if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
          }
          rafRef.current = requestAnimationFrame(() => {
            rafRef.current = null;
            flushBatch();
          });
        },
      });
      triggers.push(t);
    });

    // Refresh при зареждане – елементи вече във видимата област също се анимират
    requestAnimationFrame(() => {
      ScrollTrigger.refresh();
    });

    let refreshTimeout = null;
    const scheduleRefresh = () => {
      if (refreshTimeout !== null) {
        clearTimeout(refreshTimeout);
      }
      refreshTimeout = setTimeout(() => {
        refreshTimeout = null;
        ScrollTrigger.refresh();
      }, REFRESH_DEBOUNCE_MS);
    };
    window.addEventListener('resize', scheduleRefresh);
    window.addEventListener('orientationchange', scheduleRefresh);

    return () => {
      if (refreshTimeout !== null) {
        clearTimeout(refreshTimeout);
      }
      window.removeEventListener('resize', scheduleRefresh);
      window.removeEventListener('orientationchange', scheduleRefresh);
      triggers.forEach((t) => t.kill());
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
      lastAnimatedRef.current = -1;
      pendingSet.clear();
    };
    // refreshKey: при пренареждане на децата (напр. смяна на сортиране) DOM-ът се
    // разбърква, а тригерите пазят старите си координати — затова ефектът се
    // преинициализира, пресъздавайки тригерите спрямо новия ред и разкривайки
    // веднага картите, попаднали във видимата област.
  }, [duration, stagger, ease, start, refreshKey]);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}
